import { isDesktopBreakpoint, mediaQueryLarge } from "./utilities.js";

const CONTAINER = "[data-plp-variant-thumbs]";
const THUMB = "button[data-plp-thumb]";

/**
 * @typedef {{ url: string; title: string; price: string; mainSrc: string; hoverSrc: string; gallerySrcs: string[]; optionsCount: string; handle: string; productNo: string }} ThumbPayload
 */

/**
 * @param {HTMLButtonElement} button
 * @returns {ThumbPayload}
 */
function readThumbPayload(button) {
  const galleryRaw = button.dataset.gallerySrcs || "";
  const gallerySrcs = galleryRaw ? galleryRaw.split("###G###") : [];

  return {
    url: button.dataset.productUrl || "",
    title: button.dataset.title || "",
    price: button.dataset.price || "",
    mainSrc: button.dataset.mainSrc || "",
    hoverSrc: button.dataset.hoverSrc || "",
    gallerySrcs,
    optionsCount: button.dataset.optionsCount || "1",
    handle: button.dataset.handle || "",
    productNo: button.dataset.productNo || ""
  };
}

/**
 * @param {HTMLElement} card
 * @param {ThumbPayload} data
 */
function applyThumbPayloadToCard(card, data) {
  for (const anchor of card.querySelectorAll("a.product-card__link")) {
    if (anchor instanceof HTMLAnchorElement && data.url) {
      anchor.href = data.url;
    }
  }

  const infoLink = card.querySelector("[data-card-title-link]");
  if (infoLink instanceof HTMLAnchorElement && data.url) {
    infoLink.href = data.url;
    infoLink.textContent = data.title;
  }

  const infoWrap = card.querySelector("[data-card-info]")?.closest("a");
  if (infoWrap instanceof HTMLAnchorElement && data.url) {
    infoWrap.href = data.url;
  }

  const priceEl = card.querySelector("[data-card-price]");
  if (priceEl && data.price) {
    priceEl.textContent = data.price;
  }

  const skuEl = card.querySelector("[data-card-sku]");
  if (skuEl) {
    skuEl.textContent = data.productNo;
  }

  const mainImg = card.querySelector("[data-card-main-image] img");
  if (mainImg instanceof HTMLImageElement && data.mainSrc) {
    mainImg.src = data.mainSrc;
    mainImg.removeAttribute("srcset");
    mainImg.removeAttribute("sizes");
  }

  const hoverWrap = card.querySelector("[data-card-hover-image]");
  const hoverImg = hoverWrap?.querySelector("img");
  if (hoverWrap instanceof HTMLElement) {
    if (data.hoverSrc) {
      hoverWrap.classList.remove("hidden");
      if (hoverImg instanceof HTMLImageElement) {
        hoverImg.src = data.hoverSrc;
        hoverImg.removeAttribute("srcset");
        hoverImg.removeAttribute("sizes");
      }
    } else {
      hoverWrap.classList.add("hidden");
      if (hoverImg instanceof HTMLImageElement) {
        hoverImg.removeAttribute("src");
      }
    }
  }

  const galleryLinks = card.querySelectorAll("[data-card-images] a.product-card__link");
  if (galleryLinks.length > 0 && data.gallerySrcs.length > 0) {
    let i = 0;
    for (const link of galleryLinks) {
      const src = data.gallerySrcs[i] || data.gallerySrcs[data.gallerySrcs.length - 1] || data.mainSrc;
      for (const img of link.querySelectorAll("img")) {
        if (img instanceof HTMLImageElement && src) {
          img.src = src;
          img.removeAttribute("srcset");
          img.removeAttribute("sizes");
        }
      }
      if (link instanceof HTMLAnchorElement && data.url) {
        link.href = data.url;
      }
      i += 1;
    }
  }

  for (const qa of card.querySelectorAll("quick-add-component")) {
    if (data.url) qa.setAttribute("data-product-url", data.url);
    if (data.title) qa.setAttribute("data-product-title", data.title);
    qa.setAttribute("data-product-options-count", data.optionsCount);
  }

  for (const btn of card.querySelectorAll(".notify-me-trigger")) {
    if (data.handle) btn.setAttribute("data-product-handle", data.handle);
    if (data.title) btn.setAttribute("data-product-title", data.title);
  }
}

/**
 * @param {HTMLElement} container
 * @param {HTMLButtonElement} active
 */
function setActiveThumb(container, active) {
  for (const btn of container.querySelectorAll(THUMB)) {
    if (btn instanceof HTMLButtonElement) {
      btn.setAttribute("aria-current", btn === active ? "true" : "false");
    }
  }
}

/**
 * Desktop: extra swatches sit behind +N until toggled. Mobile: max_shown strip only; +N visible but does not expand.
 * @param {HTMLElement} thumbsContainer
 */
function syncPlpOverflowForViewport(thumbsContainer) {
  const wrap = thumbsContainer.querySelector("[data-plp-overflow-thumbs]");
  const toggle = thumbsContainer.querySelector("[data-plp-overflow-toggle]");
  if (!(wrap instanceof HTMLElement)) return;

  if (!isDesktopBreakpoint()) {
    wrap.classList.add("hidden");
    if (toggle instanceof HTMLButtonElement) {
      toggle.hidden = false;
      toggle.setAttribute("aria-disabled", "true");
      toggle.setAttribute("aria-expanded", "false");
      const collapsed = toggle.dataset.plpOverflowCollapsedLabel;
      if (collapsed) toggle.textContent = collapsed;
    }
    return;
  }

  if (toggle instanceof HTMLButtonElement) {
    toggle.removeAttribute("aria-disabled");
    toggle.hidden = false;
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    wrap.classList.toggle("hidden", !expanded);
    const collapsedLabel = toggle.dataset.plpOverflowCollapsedLabel || "";
    const expandedLabel = toggle.dataset.plpOverflowExpandedLabel || "";
    toggle.textContent = expanded ? expandedLabel : collapsedLabel;
  }
}

let plpOverflowViewportListenerAttached = false;

function ensurePlpOverflowViewportListener() {
  if (plpOverflowViewportListenerAttached) return;
  plpOverflowViewportListenerAttached = true;
  mediaQueryLarge.addEventListener("change", () => {
    for (const el of document.querySelectorAll(CONTAINER)) {
      if (!(el instanceof HTMLElement)) continue;
      syncPlpOverflowForViewport(el);
    }
  });
}

function bindThumbsContainer(thumbsContainer) {
  const card = thumbsContainer.closest("[data-product-card]");
  if (!(card instanceof HTMLElement)) return;

  thumbsContainer.addEventListener("click", (event) => {
    const toggle = event.target instanceof Element ? event.target.closest("[data-plp-overflow-toggle]") : null;
    if (toggle instanceof HTMLButtonElement && thumbsContainer.contains(toggle)) {
      if (!isDesktopBreakpoint()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const wrap = thumbsContainer.querySelector("[data-plp-overflow-thumbs]");
      const expanded = toggle.getAttribute("aria-expanded") === "true";
      const nextExpanded = !expanded;
      toggle.setAttribute("aria-expanded", nextExpanded ? "true" : "false");
      if (wrap instanceof HTMLElement) {
        wrap.classList.toggle("hidden", !nextExpanded);
      }
      const collapsedLabel = toggle.dataset.plpOverflowCollapsedLabel || "";
      const expandedLabel = toggle.dataset.plpOverflowExpandedLabel || "";
      toggle.textContent = nextExpanded ? expandedLabel : collapsedLabel;
      return;
    }

    const thumb = event.target instanceof Element ? event.target.closest(THUMB) : null;
    if (!(thumb instanceof HTMLButtonElement) || !thumbsContainer.contains(thumb)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const data = readThumbPayload(thumb);
    applyThumbPayloadToCard(card, data);
    setActiveThumb(thumbsContainer, thumb);
  });

  for (const btn of thumbsContainer.querySelectorAll(THUMB)) {
    if (!(btn instanceof HTMLButtonElement)) continue;

    btn.addEventListener("mouseenter", () => {
      if (!isDesktopBreakpoint()) return;
      const data = readThumbPayload(btn);
      applyThumbPayloadToCard(card, data);
      setActiveThumb(thumbsContainer, btn);
    });
  }

  syncPlpOverflowForViewport(thumbsContainer);
}

function initProductVariantsThumbsPlp() {
  ensurePlpOverflowViewportListener();
  for (const el of document.querySelectorAll(CONTAINER)) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.dataset.plpThumbsBound === "true") continue;
    el.dataset.plpThumbsBound = "true";
    bindThumbsContainer(el);
  }
}

initProductVariantsThumbsPlp();

document.addEventListener("shopify:section:load", initProductVariantsThumbsPlp);
